import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PhoneCall, MessageCircle, CalendarClock, Stethoscope, ClipboardCheck, HelpCircle, Bell, ShieldCheck, AlertTriangle, Plus } from "lucide-react";
import api, { fmtDay, fmtTime, inr } from "../../lib/api";
import { Loading, StatusPill } from "../../components/Shared";

export default function Overview() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/family/overview").then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Could not load")); }, []);
  if (err) return (
    <div className="p1-card p-10 text-center" data-testid="overview-empty">
      <p className="font-heading text-xl font-bold text-stone-900">{err}</p>
      <p className="mt-2 text-sm text-stone-500">New here? Complete onboarding to set up your parent's care.</p>
      <Link to="/onboarding" className="p1-btn-primary mt-5" data-testid="overview-onboarding-cta">Start Onboarding</Link>
    </div>
  );
  if (!d) return <Loading label="Loading your parent's overview…" />;
  const { parent, status, care_manager: cm, next_visit, upcoming_appointment, latest_report, open_requests, recent_activity, emergency_contacts, subscription, plan } = d;
  const bannerTone = status.state === "well" ? "border-emerald-200 bg-emerald-50" : status.state === "attention" ? "border-amber-300 bg-amber-50" : "border-sky-200 bg-sky-50";
  return (
    <div className="p1-animate-in space-y-6" data-testid="family-overview">
      <div className={`rounded-3xl border p-6 ${bannerTone}`} data-testid="parent-status-banner">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Parent overview · {((d.parents && d.parents.length ? d.parents : [parent]).filter(Boolean)).map((p) => p.name).join(" & ")}</p>
            <h1 className="mt-1 font-heading text-2xl font-bold text-stone-900 sm:text-3xl" data-testid="parent-status-message">{status.message}</h1>
            {latest_report && <p className="mt-1 text-sm text-stone-600">Last visit {fmtDay(latest_report.scheduled_at)}, {fmtTime(latest_report.check_in_at)} by {cm?.name}</p>}
          </div>
          <StatusPill status={status.state === "well" ? "active" : status.state === "attention" ? "attention_required" : "scheduled"} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="p1-card p-5" data-testid="next-visit-card">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-400"><CalendarClock className="h-4 w-4" /> Next scheduled visit</div>
              {next_visit ? (<>
                <p className="mt-2 font-heading text-lg font-semibold text-stone-900">{fmtDay(next_visit.scheduled_at)} · {fmtTime(next_visit.scheduled_at)}</p>
                <p className="text-sm text-stone-500">{next_visit.purpose}</p>
              </>) : <p className="mt-2 text-sm text-stone-500">No visit scheduled yet.</p>}
            </div>
            <div className="p1-card p-5" data-testid="upcoming-appointment-card">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-400"><Stethoscope className="h-4 w-4" /> Upcoming appointment</div>
              {upcoming_appointment ? (<>
                <p className="mt-2 font-heading text-lg font-semibold text-stone-900">{upcoming_appointment.title}</p>
                <p className="text-sm text-stone-500">{fmtDay(upcoming_appointment.when)} · {fmtTime(upcoming_appointment.when)} · {upcoming_appointment.location}</p>
              </>) : <p className="mt-2 text-sm text-stone-500">No upcoming appointments.</p>}
            </div>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="quick-actions">
            <Link to="/app/family/requests" className="p1-btn-primary !px-5 !py-2.5" data-testid="action-request-help"><HelpCircle className="h-4 w-4" /> Request Help</Link>
            {cm?.phone ? (
              <a href={`tel:${cm.phone.replace(/\s/g, "")}`} className="p1-btn-outline !px-5 !py-2.5" data-testid="action-contact-cm"><PhoneCall className="h-4 w-4" /> Contact Care Manager</a>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-full border border-dashed border-stone-300 px-5 py-2.5 text-sm text-stone-400" data-testid="action-contact-cm-pending"><PhoneCall className="h-4 w-4" /> Contact details will appear once your Care Manager is assigned.</span>
            )}
            <Link to="/app/family/requests" className="p1-btn-outline !px-5 !py-2.5" data-testid="action-book-service"><Plus className="h-4 w-4" /> Book a Service</Link>
            <Link to="/app/family/visits" className="p1-btn-outline !px-5 !py-2.5" data-testid="action-latest-update"><ClipboardCheck className="h-4 w-4" /> View Latest Update</Link>
          </div>

          {latest_report?.report && (
            <div className="p1-card p-5" data-testid="latest-report-card">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-semibold text-stone-900">Latest visit report</h2>
                <Link to={`/app/family/visits`} className="text-sm font-semibold text-emerald-900 hover:underline" data-testid="latest-report-link">Full report →</Link>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-stone-600">{latest_report.report.observations}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {(latest_report.report.tasks_completed || []).slice(0, 4).map((t) => (
                  <span key={t} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-900">{t}</span>
                ))}
              </div>
            </div>
          )}

          <div className="p1-card p-5" data-testid="recent-activity-card">
            <h2 className="font-heading text-lg font-semibold text-stone-900">Recent activity</h2>
            <ul className="mt-3 divide-y divide-stone-100">
              {recent_activity.map((e) => (
                <li key={e.id} className="flex items-start justify-between gap-4 py-3">
                  <div><p className="text-sm font-semibold text-stone-800">{e.title}</p><p className="text-xs text-stone-500">{e.detail}</p></div>
                  <span className="whitespace-nowrap text-xs text-stone-400">{fmtDay(e.at)}</span>
                </li>
              ))}
            </ul>
            <Link to="/app/family/timeline" className="mt-2 inline-block text-sm font-semibold text-emerald-900 hover:underline" data-testid="view-timeline-link">View full timeline →</Link>
          </div>
        </div>

        <div className="space-y-6">
          {cm?.name ? (
            <div className="p1-card p-5" data-testid="care-manager-card">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">Your Care Manager</p>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800" data-testid="cm-assignment-status">Currently assigned</span>
              </div>
              <div className="mt-3 flex items-center gap-3">
                {cm.photo ? (
                  <img src={cm.photo} alt={cm.name} className="h-12 w-12 rounded-full object-cover" data-testid="cm-photo" />
                ) : (
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-900 font-heading text-lg font-bold text-white">{cm.name?.[0]}</span>
                )}
                <div>
                  <p className="font-heading font-semibold text-stone-900" data-testid="cm-name">{cm.name}</p>
                  <p className="text-xs text-stone-500">
                    {(cm.languages || []).length > 0 && <>{cm.languages.join(" · ")} </>}
                    {cm.verification_status === "verified" && <span className="inline-flex items-center gap-0.5 text-emerald-700" data-testid="cm-verified"><ShieldCheck className="inline h-3 w-3" /> Verified by ParentOne</span>}
                  </p>
                </div>
              </div>
              {cm.phone ? (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <a href={`tel:${cm.phone.replace(/\s/g, "")}`} className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-900 py-2 text-xs font-semibold text-white" data-testid="cm-call-btn"><PhoneCall className="h-3.5 w-3.5" /> Call</a>
                  <a href={`https://wa.me/${cm.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 rounded-xl border border-stone-200 py-2 text-xs font-semibold text-stone-700" data-testid="cm-whatsapp-btn"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
                </div>
              ) : (
                <p className="mt-4 rounded-xl bg-stone-50 p-3 text-xs text-stone-500" data-testid="cm-contact-pending">Contact details will appear once your Care Manager is assigned.</p>
              )}
            </div>
          ) : (
            <div className="p1-card p-5" data-testid="care-manager-pending-card">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">Your Care Manager</p>
              <p className="mt-3 text-sm leading-relaxed text-stone-500">A dedicated, verified Care Manager is being assigned to your family. Their name, photo and contact options will appear here.</p>
            </div>
          )}

          <div className="p1-card p-5" data-testid="open-requests-card">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold text-stone-900">Open requests</h2>
              <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-800">{open_requests.length}</span>
            </div>
            {open_requests.length === 0 ? <p className="mt-3 text-sm text-stone-500">Nothing pending. All good.</p> : (
              <ul className="mt-3 space-y-2">
                {open_requests.slice(0, 3).map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 rounded-xl border border-stone-100 p-3">
                    <p className="text-sm font-medium text-stone-800">{r.type}</p><StatusPill status={r.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="p1-card p-5" data-testid="emergency-contacts-card">
            <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-terracotta" /><h2 className="font-heading text-lg font-semibold text-stone-900">Emergency contacts</h2></div>
            <ul className="mt-3 space-y-2">
              {emergency_contacts.sort((a, b) => a.priority - b.priority).map((c) => (
                <li key={c.id} className="flex items-center justify-between text-sm">
                  <span className="text-stone-700">{c.name} <span className="text-xs text-stone-400">({c.relation})</span></span>
                  {c.phone ? (
                    <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="font-semibold text-emerald-900" data-testid={`ec-call-${c.id}`}>{c.phone}</a>
                  ) : (
                    <span className="text-xs text-stone-400">Number on file soon</span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="p1-card p-5" data-testid="subscription-card">
            <div className="flex items-center gap-2"><Bell className="h-4 w-4 text-emerald-800" /><h2 className="font-heading text-lg font-semibold text-stone-900">Subscription</h2></div>
            {plan ? (<>
              <p className="mt-2 text-sm text-stone-700"><strong>{plan.name}</strong> · {inr(plan.price)}/{plan.period}</p>
              <p className="text-xs text-stone-500">Renews {fmtDay(subscription?.renews_at)} · <StatusPill status={subscription?.status} /></p>
            </>) : <p className="mt-2 text-sm text-stone-500">No active subscription yet.</p>}
            <Link to="/app/family/billing" className="mt-3 inline-block text-sm font-semibold text-emerald-900 hover:underline" data-testid="billing-link">Plan & Billing →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
